/* eslint-disable @typescript-eslint/no-explicit-any */
import {Box} from '@mui/material';
import {GetServerSidePropsContext} from 'next';
import ErrorPage from 'next/error';
import {useEffect, useMemo, useState} from 'react';
import {BlockRenderer} from '~/components/renderer/block-renderer';
import RenderNextImage from '~/components/wrapper/atoms/render-next-image';
import BodyArticleOrganisms from '~/components/wrapper/organisms/body-article';
import HeaderArticle from '~/components/wrapper/organisms/header-article';
import {handleReturnDateForLocal} from '~/components/wrapper/organisms/scrolling-thumbnail-card/scrolling-thumbnail-card';
import {PLACEHOLDER_IMG} from '~/constants';
import {getPageData} from '~/pages/[[...slug]]/index.page';
import {usePageSetting} from '~/providers/page-context.provider';
import {
  convertToSlug,
  handleConvertToLocaleContentful,
  handleCovertTagPrimary,
  handleTranslateNews,
} from '~/utils';
import getDataRefBySlug, {PAGE_TYPE} from 'utils/get-data-ref-by-slug';
import getListNewByGameTitle from './get-news-by-game';
import {GetNewsArticlesQuery} from '~/graphql/typescript-operations.generated';
import {client} from '~/utils/contentfulHelper';
import {handleGetProductDetail} from '~/pages/games/[slug]/get-product-detail';
import {handleReturnLocaleStore} from '~/components/wrapper/organisms/home-page-mash-thead/home-page-mash-thead';

const NewsArticles = (props: any) => {
  const {data, detailData, host, locale, slug, bcProductReference, listNews, entryData} = props;
  const page = data?.pageCollection?.items[0];
  const detailItem = detailData?.data?.newsCollection?.items[0];
  const [layoutProtect, setLayoutProtect] = useState(detailItem?.isProtectedPassword);

  const {globalData, setDataSlug} = usePageSetting();
  const dataTranslation = globalData?.dataTranslation?.[0] ?? {};
  const listTagsColor = globalData?.listTagsColor ?? [];
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    setDataSlug(entryData ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryData]);

  if (!page) {
    return <ErrorPage statusCode={404} />;
  }

  const handleDisableProtect = () => {
    setLayoutProtect(false);
  };

  const handleShowDate = () => {
    if (detailItem?.fromDate && detailItem?.toDate) {
      return `${handleReturnDateForLocal(locale, `${detailItem?.fromDate}`.substring(0, 10))} ${
        dataTranslation['news-detail-date-range']
      } ${handleReturnDateForLocal(locale, `${detailItem?.toDate}`.substring(0, 10))} `;
    }

    if (detailItem?.fromDate && !detailItem?.toDate) {
      return `${
        locale?.toLocaleLowerCase() !== 'hk-tc' &&
        locale?.toLocaleLowerCase() !== 'hk-sc' &&
        locale?.toLocaleLowerCase() !== 'tw-tc' &&
        locale?.toLocaleLowerCase() !== 'hk-sc' &&
        locale?.toLocaleLowerCase() !== 'ko-kr'
          ? 'From'
          : ''
      } ${handleReturnDateForLocal(locale, `${detailItem?.fromDate}`.substring(0, 10))}
      ${
        locale?.toLocaleLowerCase() === 'ko-kr'
          ? '부터'
          : locale?.toLocaleLowerCase() === 'tw-sc' || locale?.toLocaleLowerCase() === 'hk-sc'
          ? '起'
          : locale?.toLocaleLowerCase() === 'tw-tc' || locale?.toLocaleLowerCase() === 'hk-tc'
          ? '起'
          : ''
      }
      `;
    }
  };

  const blogHeader = {
    __typename: 'HeaderArticle',
    title: detailItem?.title,
    hideMasthead: !!detailItem?.hideMasthead,
    date: {
      text: dataTranslation['news-detail-published-on'],
      value: handleReturnDateForLocal(locale, `${detailItem?.publishDate}`.substring(0, 10)),
    },
    mainImage: detailItem?.mainImage?.media?.url ?? '',
    mainImageNext: (
      <RenderNextImage
        src={detailItem?.mainImage?.media?.url ?? PLACEHOLDER_IMG}
        width={1280}
        height={700}
        alt={detailItem?.title || ''}
      />
    ),
    timeRange: handleShowDate(),
    downloadTitle: dataTranslation['news-detail-download-all'] ?? 'Download all',
    store: {
      url:
        detailItem?.shopNowUrl && `${detailItem?.shopNowUrl}`.includes('http')
          ? detailItem?.shopNowUrl
          : detailItem?.shopNowUrl
          ? `${process.env.NEXT_PUBLIC_STORE_SITE_URL}${handleReturnLocaleStore(
              locale || ''
            )}/store/games`
          : bcProductReference?.title
          ? `${process.env.NEXT_PUBLIC_STORE_SITE_URL}${handleReturnLocaleStore(
              locale || ''
            )}/store/games/${convertToSlug(bcProductReference?.title)}`
          : '',
      title: detailItem?.shopNowUrl
        ? dataTranslation['home-promotion-banner-shop-now'] || 'Shop Now'
        : bcProductReference?.title
        ? dataTranslation['home-promotion-banner-shop-now'] || 'Shop Now'
        : '',
    },
    idVideo: detailItem?.mainImage?.videoEmbed || '',
    tags: [
      {
        title: handleTranslateNews(detailItem?.customTags, dataTranslation, detailItem, locale),
        color:
          listTagsColor?.find(
            (e: any) =>
              e.name?.toUpperCase() ===
              (
                handleCovertTagPrimary(
                  (detailItem?.customTags as string)?.toUpperCase(),
                  detailItem?.game?.releaseDate ?? '',
                  locale as string
                ) as string
              )?.toUpperCase()
          )?.backgroundColor ?? '',
      },
    ],
    fbShare: `https://www.facebook.com/sharer/sharer.php?u=${host}/news/${slug}&t=${detailItem?.title}`,
    // instaShare: `https://www.instagram.com/?url=${host}/news/${slug}`,
    instaShare: '',
    twShare: `https://twitter.com/intent/tweet?text=${host}/news/${slug}`,
    weiboShare: `https://service.weibo.com/share/share.php?url=${host}/news/${slug}&title=Share&language=zh_cn#_loginLayer_1662605727516`,
    kakaoShare: `https://story.kakao.com/s/share?url=${host}/news/${slug}&text=Share&app_key=BLAH2`,
    label: dataTranslation['news-detail-share-this-article'],
    titleCopiedToClipboard: dataTranslation['copied-to-clipboard'],
  };

  const blogBody = {
    __typename: 'BodyArticle',
    label: dataTranslation['news-detail-share-this-article'],
    content: detailItem?.content ?? '',
    customTags: (detailItem?.secondaryTags ?? []).map((value: any) => {
      return handleTranslateNews(value, dataTranslation, detailItem, locale);
    }),
    fbShare: `https://www.facebook.com/sharer/sharer.php?u=${host}/news/${slug}`,
    // instaShare: `https://www.instagram.com/?url=${host}/news/${slug}`,
    instaShare: '',
    twShare: `https://twitter.com/intent/tweet?text=${host}/news/${slug}`,
    weiboShare: `https://service.weibo.com/share/share.php?url=${host}/news/${slug}&title=Share&language=zh_cn#_loginLayer_1662605727516`,
    kakaoShare: `https://story.kakao.com/s/share?url=${host}/news/${slug}&text=Share&app_key=BLAH2`,
    passwordDescription: detailItem?.passwordDescription,
    isProtectedPassword: detailItem?.isProtectedPassword,
    idNew: detailItem?.idNew,
    dataTranslation: dataTranslation,
    titleCopiedToClipboard: dataTranslation['copied-to-clipboard'],
    uiContent: detailItem?.uiContent,
  };

  const exploreMore = {
    __typename: listNews?.items.length > 0 ? 'ExploreMore' : '',
    title: dataTranslation['news-detailed-explore-more-title'],
    viewMore: {
      title: dataTranslation['news-detailed-explore-see-more-news'],
      href: `/${locale}/news`,
      target: '_blank',
    },
    listTags: listTagsColor,
    items: handleTranslateNews(listNews?.items, dataTranslation),
  };

  // const subscribe = {
  //   __typename: 'SubscribePopup',
  // };
  const thumbnailImageUrl =
    (detailData?.data?.newsCollection?.items[0]?.game?.gamesFilterThumbnailImage?.media?.url ||
      detailData?.data?.newsCollection?.items[0]?.game?.mainThumbnailImg?.media?.url) ??
    PLACEHOLDER_IMG;
  const gameSuggestionsCard = detailItem &&
    bcProductReference?.title && {
      __typename: 'Section',
      noPadding: false,
      internalName: 'game|game-suggestions-card',
      sectionWrapperStyles: {
        backgroundColor: '#F9F9F9',
      },
      blocksCollection: {
        items: [
          {
            ...bcProductReference?.site?.product,
            bcProductReference,
            name:
              detailData?.data?.newsCollection?.items[0]?.game?.title ??
              bcProductReference?.site?.product?.name,
            platform: detailData?.data?.newsCollection?.items[0]?.game?.platform ?? [],
            gamesFilterThumbnailImage: thumbnailImageUrl,
            slugGame: detailData?.data?.newsCollection?.items[0]?.game?.slug,
            __typename: 'GameSuggestionsCard',
            dataTranslation: dataTranslation,
          },
        ],
      },
    };

  const seoStandard = {
    __typename: 'SeoStandard',
    seoTitle: detailItem?.title,
    seoDescription: detailItem?.shortDescription,
    seoRobots: null,
    seoImage: thumbnailImageUrl + '?w=1200&fit=fill',
    ogTitle: detailItem?.title,
    ogDescription: detailItem?.shortDescription,
    ogImage: thumbnailImageUrl + '?w=1200&fit=fill',
    seoKeywords: detailItem?.genre,
    seoAuthor: 'Bandai Namco Entertainment Asia',
  };

  const block: Array<any> = [seoStandard, gameSuggestionsCard, exploreMore].filter(
    block => !!block
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const HeaderArticleCallBack = useMemo(() => {
    return <HeaderArticle {...blogHeader} isProtect={layoutProtect} title={detailItem?.title} />;
  }, [layoutProtect, dataTranslation]);

  return (
    <Box>
      {HeaderArticleCallBack}
      <BodyArticleOrganisms {...blogBody} handleDisableProtect={handleDisableProtect} />
      <BlockRenderer block={block} />
    </Box>
  );
};

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const CONTENTFUL_SITE_URL = process.env.NEXT_PUBLIC_SITE_URL;
  const slug = String(ctx?.params?.slug ?? '/');
  const preview = !!(process && process.env && process.env.NEXT_PUBLIC_PREVIEW);
  ctx.res.setHeader('Cache-Control', 'public, s-maxage=60, stale-while-revalidate=120');

  const promises: [ReturnType<typeof getDataRefBySlug>, ReturnType<typeof getPageData>] = [
    getDataRefBySlug({slug: slug, preview}, ctx, PAGE_TYPE.News),
    getPageData({slug: 'news', preview}, ctx),
  ];
  const [detailData, dataPage] = await Promise.all(promises);
  const pageDetailData = detailData?.data as GetNewsArticlesQuery;
  const idProd = pageDetailData.newsCollection?.items[0]?.game?.bcProductReference ?? '';
  const gameTitle = pageDetailData.newsCollection?.items[0]?.game?.title ?? '';
  const listNews = await getListNewByGameTitle(
    gameTitle,
    handleConvertToLocaleContentful(ctx.locale as string),
    3,
    0,
    slug
  );
  let entryData: any = {};
  try {
    entryData =
      pageDetailData.newsCollection?.items[0]?.slug &&
      (await client(
        process.env.NEXT_PUBLIC_CF_SPACE_ID as string,
        process.env.NEXT_PUBLIC_CF_DELIVERY_ACCESS_TOKEN as string
      ).getEntry(pageDetailData?.newsCollection?.items[0]?.sys?.id ?? '', {
        locale: '*',
      }));
  } catch (error) {
    console.log(error);
  }

  if (!pageDetailData.newsCollection?.items[0]?.slug) {
    return {
      redirect: {
        destination: `/${ctx.locale}/404`,
        permanent: false,
      },
    };
  }
  const currencyCode = ['tw-tc', 'tw-sc'].includes(`${ctx?.locale}`.toLowerCase())
    ? 'TWD'
    : ['hk-tc', 'hk-sc'].includes(`${ctx?.locale}`.toLowerCase())
    ? 'HKD'
    : ['ko-kr'].includes(`${ctx?.locale}`.toLowerCase())
    ? 'KRW'
    : 'SGD';

  const bcProductReference = idProd
    ? await handleGetProductDetail(
        idProd,
        handleConvertToLocaleContentful(ctx.locale as string),
        currencyCode as string
      )
    : {};

  const {data} = dataPage;
  return {
    props: {
      data,
      detailData,
      host: CONTENTFUL_SITE_URL,
      locale: ctx?.locale,
      slug: slug,
      listNews: listNews,
      bcProductReference,
      entryData: entryData?.fields?.slug ?? {},
    },
  };
};

export default NewsArticles;
